import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiHosts } from "@/api/constants/apiHosts";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";
import type { EnforcementAction, EnforcementActionNew, EnforcementActionQuery } from "./enforcementAction";
import enforcementActionService from "./enforcementActionService";

const URL = "enforcement-actions";
const ENTITY_NAME = "Enforcement Action";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<EnforcementAction>(apiHosts.AQMS, axiosParams);

const getEnforcementActions = (params: EnforcementActionQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

type EnforcementActionIds = {
  id?: string | number;
};
const getNewEnforcementAction = async (ids: EnforcementActionIds) => {
  const defaultApp = {
    data: {
      ...enforcementActionService.getInitialData(),
      ...ids,
      typeMurn: LE.EnforcementActionType.Non,
    },
  } as AxiosResponse<EnforcementActionNew, any>;

  return defaultApp;
};

const getEnforcementActionById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const createEnforcementAction = (enforcementAction: EnforcementAction, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, enforcementAction, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateEnforcementAction = (enforcementAction: EnforcementAction, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + enforcementAction.id, enforcementAction, {
    successNotifCb,
    errorNotifCb,
  });
};

// const getEnforcementActionByIdOrNew = (ids: EnforcementAction, config: PagingApiAndNotifierConfig = {}) => {
//   if (!ids.id) {
//     return getNewEnforcementAction(ids);
//   } else {
//     return getEnforcementActionById(ids.id, config) as Promise<AxiosResponse<EnforcementActionNew, any>>;
//   }
// };

/**
 * @param {EnforcementAction} enforcementAction This cannot be a 'ref' to a enforcementAction
 */
const saveEnforcementAction = (enforcementAction: EnforcementAction, config = {}) => {
  if (enforcementAction.id) {
    return updateEnforcementAction(enforcementAction, config);
  } else {
    return createEnforcementAction(enforcementAction, config);
  }
};

const deleteEnforcementActionById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  deleteEnforcementActionById,
  getEnforcementActions,
  getEnforcementActionById,
  getNewEnforcementAction,
  // getEnforcementActionByIdOrNew,
  createEnforcementAction,
  updateEnforcementAction,
  saveEnforcementAction,
};
